import { CuriosityAutocomplete, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import { ValueEditorInput, ValueEditorNumberInput, ValueEditorNumberRange } from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CommodityGroupFilterField } from '@/generated/gql/graphql';
import { CommodityGroupQueryFieldGroup } from '~/common/types';
import {
  listOperators,
  operators,
  queryFieldFactory,
  UseDataGridQueryBuilderConfig,
} from '~/components/DataGridQueryBuilder';

const NAMESPACE_MAP = {
  [CommodityGroupFilterField.id]: CuriosityNamespaceType.COMMODITY_GROUP,
  [CommodityGroupFilterField.sourceSystemId]: CuriosityNamespaceType.SOURCE_SYSTEM,
};

export const useCommodityGroupDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation();

  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'Base.commodityGroup' });
    return {
      fields: [
        // ---------- COMMODITY GROUP----------
        createField({
          name: CommodityGroupFilterField.id,
          grp: CommodityGroupQueryFieldGroup.commodityGroup,
          operators: operators.idAuto,
        }),
        createField({
          name: CommodityGroupFilterField.description,
          grp: CommodityGroupQueryFieldGroup.commodityGroup,
          operators: operators.text,
        }),
        // ---------- ORDERS ----------
        createField({
          name: CommodityGroupFilterField.orderCountCurrentYear,
          grp: CommodityGroupQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityGroupFilterField.orderCountLast12Months,
          grp: CommodityGroupQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityGroupFilterField.orderCountLastYear,
          grp: CommodityGroupQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityGroupFilterField.orderVolumeCurrentYear,
          grp: CommodityGroupQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityGroupFilterField.orderVolumeLast12Months,
          grp: CommodityGroupQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityGroupFilterField.orderVolumeLastYear,
          grp: CommodityGroupQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityGroupFilterField.sourceSystemId,
          grp: CommodityGroupQueryFieldGroup.commodityGroup,
          operators: operators.idAuto,
        }),
        createField({
          name: CommodityGroupFilterField.orderVolumeShareCurrentYear,
          grp: CommodityGroupQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityGroupFilterField.orderVolumeShareLastYear,
          grp: CommodityGroupQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityGroupFilterField.orderVolumeShareLast12Months,
          grp: CommodityGroupQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityGroupFilterField.orderVolumeCumulativeCurrentYear,
          grp: CommodityGroupQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityGroupFilterField.orderVolumeCumulativeLastYear,
          grp: CommodityGroupQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: CommodityGroupFilterField.orderVolumeCumulativeLast12Months,
          grp: CommodityGroupQueryFieldGroup.order,
          operators: operators.num,
        }),
      ],
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }
        switch (field) {
          case CommodityGroupFilterField.id:
          case CommodityGroupFilterField.sourceSystemId:
            return (
              <CuriosityAutocomplete
                ns={NAMESPACE_MAP[field]}
                multiple={listOperators[operator]}
                simplifyValue
                {...otherProps}
              />
            );
          case CommodityGroupFilterField.name:
          case CommodityGroupFilterField.description:
            return <ValueEditorInput {...otherProps} />;

          case CommodityGroupFilterField.orderVolumeLastYear:
          case CommodityGroupFilterField.orderVolumeCurrentYear:
          case CommodityGroupFilterField.orderVolumeLast12Months:
          case CommodityGroupFilterField.orderCountCurrentYear:
          case CommodityGroupFilterField.orderCountLast12Months:
          case CommodityGroupFilterField.orderCountLastYear:
          case CommodityGroupFilterField.orderVolumeShareCurrentYear:
          case CommodityGroupFilterField.orderVolumeShareLastYear:
          case CommodityGroupFilterField.orderVolumeShareLast12Months:
          case CommodityGroupFilterField.orderVolumeCumulativeCurrentYear:
          case CommodityGroupFilterField.orderVolumeCumulativeLastYear:
          case CommodityGroupFilterField.orderVolumeCumulativeLast12Months:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorNumberRange {...otherProps} />;
            }
            return <ValueEditorNumberInput min={0} {...otherProps} />;
          default:
            return null;
        }
      },
    };
  }, [t]);
};
